import { Link } from "@tanstack/react-router";
import {
  ArrowUpRight,
  Check,
  Circle,
  Copy,
  Globe2,
  ShieldCheck,
} from "lucide-react";
import { useConsoleContext } from "../shell";

export function SitesPage() {
  const {
    activeOrganization,
    activeOrganizationId,
    sites,
    sitesLoading,
  } = useConsoleContext();
  const selectedSite = sites[0];
  const deploymentSteps = selectedSite
    ? [
        {
          title: "Create website record",
          detail: "Tenant, environment, currency and outcome",
          complete: true,
        },
        {
          title: `Verify ${selectedSite.origin?.replace(/^https?:\/\//, "") ?? selectedSite.name}`,
          detail: "Publish DNS TXT or the provided meta tag",
          complete: selectedSite.status !== "draft" && selectedSite.status !== "verifying",
        },
        {
          title: "Connect authoritative outcomes",
          detail:
            selectedSite.outcome_type === "booking"
              ? "Authorize Lodgify and verify signed lifecycle receipts"
              : "Map a versioned qualified outcome receipt",
          complete: false,
        },
        {
          title: "Install the async SDK",
          detail: "Configuration fetch only; rendering remains locked",
          complete: selectedSite.status === "shadow" || selectedSite.status === "live",
        },
        {
          title: "Start two-week shadow window",
          detail: "Decisions recorded, visitor experience unchanged",
          complete: selectedSite.status === "shadow" || selectedSite.status === "live",
        },
      ]
    : [];
  const completedSteps = deploymentSteps.filter((step) => step.complete).length;

  return (
    <div className="page-frame sites-page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">WEBSITES · {sites.length}</p>
          <h1>Connect each client’s proving ground.</h1>
          <p className="page-intro">
            A website is not live when a tag loads. It is live when origin,
            consent, outcome, holdout and rollback have receipts.
          </p>
        </div>
        <button className="secondary-action" type="button" disabled>
          Add website <span>{activeOrganization.tier.toUpperCase()} ACCESS</span>
        </button>
      </section>

      <section className="site-table" aria-label="Websites">
        <div className="site-table-head">
          <span>WEBSITE</span>
          <span>OUTCOME</span>
          <span>STATE</span>
          <span>ENVIRONMENT</span>
          <span />
        </div>
        {sitesLoading ? (
          <p className="table-empty">Reading client websites…</p>
        ) : sites.length === 0 ? (
          <p className="table-empty">No websites have been created for this client.</p>
        ) : (
          sites.map((site, index) => (
            <div
              className={`site-table-row ${index === 0 ? "is-selected" : ""}`}
              key={site.id}
            >
              <div className="domain-cell">
                <Globe2 size={16} />
                <span>
                  <b>{site.origin?.replace(/^https?:\/\//, "") ?? site.name}</b>
                  <small>{site.name}</small>
                </span>
              </div>
              <span>{site.outcome_type}</span>
              <span className={`status-chip ${site.status === "shadow" || site.status === "live" ? "teal" : ""}`}>
                {site.status.toUpperCase()}
              </span>
              <span className="environment-cell">
                <i /> Production
              </span>
              {site.outcome_type === "booking" ? (
                <Link
                  className="site-open-link"
                  to="/organizations/$organizationId/sites/$siteId/integrations/lodgify"
                  params={{ organizationId: activeOrganizationId, siteId: site.id }}
                  aria-label={`Connect outcomes for ${site.name}`}
                >
                  <ArrowUpRight size={16} />
                </Link>
              ) : (
                <button type="button" disabled aria-label={`${site.name} connector is not yet available`}>
                  <ArrowUpRight size={16} />
                </button>
              )}
            </div>
          ))
        )}
      </section>

      {selectedSite ? (
        <div className="setup-grid">
          <section className="setup-sequence">
            <div className="section-heading">
              <div>
                <p className="eyebrow">{selectedSite.name}</p>
                <h2>Production readiness</h2>
              </div>
              <strong>{completedSteps} / {deploymentSteps.length}</strong>
            </div>
            <div className="setup-steps">
              {deploymentSteps.map((step, index) => (
                <div className={step.complete ? "setup-step is-complete" : "setup-step"} key={step.title}>
                  <span>{step.complete ? <Check size={14} /> : <Circle size={12} />}</span>
                  <div>
                    <b>{step.title}</b>
                    <small>{step.detail}</small>
                  </div>
                  <em>0{index + 1}</em>
                </div>
              ))}
            </div>
          </section>

          <aside className="install-receipt">
            <p className="eyebrow">PUBLIC SITE KEY</p>
            <div className="copy-field">
              <code>{selectedSite.public_key}</code>
              <button type="button" disabled aria-label="Copy site key">
                <Copy size={14} />
              </button>
            </div>
            <div className="receipt-divider" />
            <p className="eyebrow">CURRENT BOUNDARY</p>
            <div className="boundary-row"><span>Configuration</span><b>INERT</b></div>
            <div className="boundary-row"><span>Event ingestion</span><b>ORIGIN LOCKED</b></div>
            <div className="boundary-row"><span>Intervention render</span><b>DISABLED</b></div>
            <div className="boundary-row"><span>AI</span><b>UNAVAILABLE</b></div>
            <div className="receipt-note">
              <ShieldCheck size={16} />
              <span>
                <b>Rendering remains locked.</b>
                <small>Outcome evidence and shadow validation must clear their control gates first.</small>
              </span>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
